import React from 'react';
import { Translate } from 'react-redux-i18n';
import get from 'lodash.get';
import { Route, Footprints, Clock, Repeat } from 'lucide-react';
import { ViewTable } from '../layouts/viewTable';
import { ViewSectionRow } from '../layouts/viewSectionRow';

interface Props { entity: any; isShowEmptyFields: boolean; translationBase: string; }
export const PageEntityFitnessDetails: React.FC<Props> = ({ entity, isShowEmptyFields, translationBase }) => {
  const targets = get(entity,'fitnessSetup.targets', []);
  const getTarget = (key: string) => targets?.find((t: any) => t.key === key)?.target || null;
  
  const distance = get(entity,'fitness.distance.alternatives')?.find((i: any) => i.key === 'KmRawValue')?.value?.[0] || null;
  const distanceTarget = getTarget('distance');
  
  const steps = get(entity,'fitness.rawData.stepsRawValue') || null;
  const stepsTarget = getTarget('numberOfSteps');
  
  const time = get(entity,'fitness.time.alternatives')?.find((i: any) => i.key === 'HourRawValue')?.value?.[0] || null;
  const timeDisplay = get(entity,'fitness.time.alternatives')?.find((i: any) => i.key === 'HoursAndMinutesElapsed')?.value || [];
  const timeTarget = getTarget('time');
  
  const reps = get(entity,'fitness.repetitions.rawValue') || null;
  const repsTarget = getTarget('repetitions');
  
  const hasContent = distance || steps || time || reps || distanceTarget || stepsTarget || timeTarget || repsTarget;
  if (!hasContent && !isShowEmptyFields) return null;

  // Helper function to format metric display
  const formatMetric = (value: number | null, target: number | null, unit: string, customDisplay?: string) => {
    const displayValue = customDisplay || (value !== null ? `${value} ${unit}` : `0 ${unit}`);
    
    if (target !== null) {
      const percentage = target > 0 ? Math.round(((value || 0) / target) * 100) : 0;
      return `${displayValue} / ${target} ${unit} (${percentage}%)`;
    }
    
    return displayValue;
  };

  // Format time display
  const timeDisplayString = timeDisplay.length === 2 && timeTarget 
    ? `${timeDisplay[0]} hr(s) ${timeDisplay[1]} min(s)`
    : timeDisplay[0] 
    ? `${timeDisplay[0]} hr(s)`
    : time !== null 
    ? `${time} hr(s)`
    : '0 hr(s)';

  const distanceDisplay = (distance > 0 || distanceTarget || isShowEmptyFields) ? (
    <div className="fundraising-metric-row">
      <Route size={18} />
      <span>{formatMetric(distance, distanceTarget, 'km')}</span>
    </div>
  ) : null;

  const stepsDisplay = (steps > 0 || stepsTarget || isShowEmptyFields) ? (
    <div className="fundraising-metric-row">
      <Footprints size={18} />
      <span>{formatMetric(steps, stepsTarget, 'steps')}</span>
    </div>
  ) : null;

  const timeDisplayFormatted = (time > 0 || timeTarget || isShowEmptyFields) ? (
    <div className="fundraising-metric-row">
      <Clock size={18} />
      <span>{formatMetric(time, timeTarget, 'hr(s)', timeDisplayString)}</span>
    </div>
  ) : null;

  const repsDisplay = (reps > 0 || repsTarget || isShowEmptyFields) ? (
    <div className="fundraising-metric-row">
      <Repeat size={18} />
      <span>{formatMetric(reps, repsTarget, 'reps')}</span>
    </div>
  ) : null;

  return (
    <div className="page-entity-fitness-details">
      <ViewTable title={<Translate value={`${translationBase}.fitness.title`} />}>
        {distanceDisplay && (
          <ViewSectionRow 
            title={`${translationBase}.fitness.distance`} 
            data={distanceDisplay} 
            isShowEmptyFields={isShowEmptyFields} 
          />
        )}
        {stepsDisplay && (
          <ViewSectionRow 
            title={`${translationBase}.fitness.steps`} 
            data={stepsDisplay} 
            isShowEmptyFields={isShowEmptyFields} 
          />
        )}
        {timeDisplayFormatted && (
          <ViewSectionRow 
            title={`${translationBase}.fitness.time`} 
            data={timeDisplayFormatted} 
            isShowEmptyFields={isShowEmptyFields} 
          />
        )}
        {repsDisplay && (
          <ViewSectionRow 
            title={`${translationBase}.fitness.reps`} 
            data={repsDisplay} 
            isShowEmptyFields={isShowEmptyFields} 
          />
        )}
      </ViewTable>
    </div>
  );
};
export default PageEntityFitnessDetails;
